import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useAxiosAuth0 } from "@axios-config/useAxiosAuth0";
import { useAppSelector } from "@redux/hooks";
import GenericTabs from "@ui-components/Tabs/GenericTabs";
import { usePageHeaderActions } from "@shared/navigation/PageHeaderActions";
import { InsightLayout } from "../components/InsightLayout";
import { InsightScopeBrowser } from "../components/TopologyV2/InsightScopeBrowser";
import { InsightTopologyCanvas } from "../components/TopologyV2/InsightTopologyCanvas";
import { NetworkTopologyTour } from "../components/TopologyV2/NetworkTopologyTour";
import { InsightResourceList } from "../components/TopologyV2/InsightResourceList";
import { shouldDefaultToList } from "../components/TopologyV2/insightResourceRows";
import { ResourceDetailPanel } from "../components/TopologyV2/ResourceDetailPanel";
import { FindingsPanel } from "../components/TopologyV2/FindingsPanel";
import { StatusBanner } from "../components/TopologyV2/StatusBanner";
import { CoverageWarningBanner } from "../components/TopologyV2/CoverageWarningBanner";
import { useInsightScopeUrlSync } from "../hooks/useInsightScopeUrlSync";
import { useInsightAsync } from "../hooks/useInsightAsync";
import { getFindings, getTopology, listTopologyScopes } from "../services/insightApi";
import { formatScope, resolveProcessedScope } from "../utils/insightScopeReconcile";
import { describeEmptyState } from "../components/TopologyV2/insightEmptyState";
import { ClusterMemberSelector } from "../components/TopologyV2/ClusterMemberSelector";
import type {
	InsightEnvelope,
	InsightFindingsResponse,
	InsightProvenanceMember,
	InsightScopeTuple,
	InsightSourceType,
	InsightTopologyResponse,
	InsightTopologyScopesResponse,
} from "../types/insightApi";

/**
 * Source-first Insight Topology view (CTX-97) — new production view shell
 * consuming the CTX-96/CTX-99 `chopper-backend` `/api/v2/insight/*` contract
 * instead of the legacy `public/data/tenants/9` static artifacts consumed by
 * `ctxReflectorV2/views/TopologyView.tsx` (still the default at
 * `/insight/topology`; this ships as Reflector v2.0 -> Network Topology,
 * GA in every environment since 2026-08-06).
 *
 * Selection: `InsightScopeBrowser` reuses BGP Visibility's authenticated
 * Cloud -> Account -> Region -> Reflector/Cluster sidebar tree end to end
 * (CTX-97 "reuse the existing BGP Visibility inventory pipeline"
 * correction) — a leaf click carries the whole path atomically via
 * `activateScope`, so there is no separate cloud/region/account cascade
 * here. `insightScopeSlice` (shared across Topology/Findings here, and
 * available to Inventory/Path Analysis/Optimization/AI surfaces later) is
 * mirrored to the URL by `useInsightScopeUrlSync` so a refreshed deep link
 * restores the full selection.
 *
 * A deep link can still carry an untrusted/incomplete scope (e.g. a
 * hand-edited URL missing `cloud`/`region`) — that renders an explicit
 * "incomplete scope" state below rather than firing a request that's
 * guaranteed to fail. A *complete* but unauthorized/foreign scope is instead
 * proven wrong by the backend itself: `getTopology`/`getFindings` 401/403/404
 * responses map to the "unauthorized"/"not-found" states via
 * `useInsightAsync`'s `classifyError`, so tenant isolation is enforced
 * server-side, never guessed client-side.
 */
export const InsightTopologyViewV2: React.FC = () => {
	const api = useAxiosAuth0();
	const tenantId = useAppSelector((state) => state.selection.tenantId);
	const { scope, activateScope, selectResource } = useInsightScopeUrlSync();
	const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
	// V1 scope cut (2026-08-04): the header search input and the Findings tab
	// are hidden for the preliminary release — `search` stays wired to the
	// canvas/list (empty = no filtering) so restoring the input is trivial,
	// and the view always renders the topology mode regardless of any
	// persisted `scope.mode`.
	const [search] = useState("");
	const activeMode: "topology" | "findings" = "topology";
	// null = follow the per-source auto default (`shouldDefaultToList`); a
	// concrete value = the user picked a representation and it wins until they
	// switch source (see the reset effect below).
	const [representationOverride, setRepresentationOverride] = useState<"canvas" | "list" | null>(null);
	/**
	 * Cluster member scoping: `null` shows the merged topology of every member
	 * (the default), a ctxrUuid re-scopes the REQUEST to that member alone, as a
	 * `reflector` source. The sidebar selection stays on the cluster either way,
	 * so the member switcher never fights the source picker.
	 */
	const [memberCtxrUuid, setMemberCtxrUuid] = useState<string | null>(null);
	/**
	 * Members of the cluster currently in scope, captured from the last MERGED
	 * response's provenance — a member-scoped response only lists itself, so the
	 * switcher would otherwise lose its other tabs after the first switch.
	 */
	const [clusterMembers, setClusterMembers] = useState<InsightProvenanceMember[]>([]);

	const hasSource = scope.sourceType != null && scope.sourceId != null;

	// A new source starts back on its own auto default rather than inheriting the
	// previous source's manual choice — a nested VPC topology should reopen on
	// the canvas even if the last (flat backbone) source was switched to List.
	useEffect(() => {
		setRepresentationOverride(null);
	}, [scope.sourceType, scope.sourceId]);

	// Switching source drops any member scoping and the remembered member list:
	// they belong to the cluster that was in scope, not to the new selection.
	useEffect(() => {
		setMemberCtxrUuid(null);
		setClusterMembers([]);
	}, [scope.sourceType, scope.sourceId]);

	/**
	 * The source the topology/findings requests actually ask for. Normally the
	 * scope itself; when a cluster member is selected it becomes that member as a
	 * standalone `reflector`, so the backend lays out one member's topology
	 * instead of the merged cluster.
	 */
	const requestedSource = useMemo(
		() =>
			memberCtxrUuid
				? { sourceType: "reflector" as InsightSourceType, sourceId: memberCtxrUuid }
				: { sourceType: scope.sourceType, sourceId: scope.sourceId },
		[memberCtxrUuid, scope.sourceType, scope.sourceId]
	);

	/**
	 * The scope the URL / sidebar asked for, as a comparable tuple. The sidebar
	 * builds it from the reflector's Cloud/Account/Region ancestry in the BGP
	 * Visibility inventory tree, which says where the reflector *lives* — not
	 * necessarily what discovery *processed* (CTX-424).
	 */
	const requestedTuple = useMemo<InsightScopeTuple | null>(
		() => (scope.cloud && scope.region ? { provider: scope.cloud, region: scope.region, account: scope.account } : null),
		[scope.cloud, scope.region, scope.account]
	);

	/**
	 * The scopes this source has actually processed. `listTopologyScopes` shipped
	 * with CTX-97 as a tested contract shape "in case a future health/freshness
	 * enrichment pass wants them" (see `services/insightApi.ts`) — this is that
	 * pass, and the first runtime caller.
	 */
	const scopesFetcher = useCallback(
		(signal: AbortSignal) => {
			if (!requestedSource.sourceType || requestedSource.sourceId == null) {
				return Promise.reject(new Error("no source"));
			}
			return listTopologyScopes(
				api,
				{
					sourceType: requestedSource.sourceType,
					sourceId: requestedSource.sourceId,
					tenantId: tenantId || undefined,
				},
				signal
			).then((response) => response.data);
		},
		[api, tenantId, requestedSource.sourceType, requestedSource.sourceId]
	);
	const scopesResult = useInsightAsync<InsightEnvelope<InsightTopologyScopesResponse>>(
		hasSource ? scopesFetcher : null,
		[tenantId, requestedSource.sourceType, requestedSource.sourceId]
	);

	/**
	 * Which processed scope the topology request should use. `redirected` is the
	 * production reproduction: `CTX-2.1-TEST` sits under account 940761539651 in
	 * the inventory while its only digest describes 734722513437 with 112 nodes,
	 * so the request used to ask for a scope that could only ever come back
	 * empty. Until the scopes call resolves we hold the request rather than fire
	 * one we may be about to correct.
	 */
	const scopeResolution = useMemo(
		() => (scopesResult.data ? resolveProcessedScope(requestedTuple, scopesResult.data.data.scopes) : null),
		[scopesResult.data, requestedTuple]
	);
	const scopesSettled = scopesResult.status !== "loading" && scopesResult.status !== "idle";

	/**
	 * Optimistic, then corrected. The topology request goes out immediately with
	 * the requested scope — exactly as it did before this reconciliation existed,
	 * so the healthy path keeps its timing and the header/canvas never blank out
	 * waiting on a second call. When the scopes response then says the request
	 * pointed somewhere unprocessed, `effectiveScope` changes and the request
	 * re-fires against the right scope; `useInsightAsync`'s deps below are the
	 * scope *primitives*, so a `match` (same values, new object) does not refetch.
	 *
	 * Costs one wasted request in the mismatch case. Worth it: holding every
	 * request behind the scopes call would delay the 99% healthy path to protect
	 * the exception, and a failed scopes call would strand the view.
	 */
	const effectiveScope = useMemo<InsightScopeTuple | null>(() => {
		if (!scopeResolution) return requestedTuple;
		if (scopeResolution.kind === "match" || scopeResolution.kind === "redirected") return scopeResolution.scope;
		// `none` / `ambiguous`: no request can succeed, so block it and let the
		// explicit states below explain why.
		return null;
	}, [scopeResolution, requestedTuple]);

	const topologyFetcher = useCallback(
		(signal: AbortSignal) => {
			if (!requestedSource.sourceType || requestedSource.sourceId == null || !effectiveScope) {
				return Promise.reject(new Error("scope incomplete"));
			}
			return getTopology(
				api,
				{
					sourceType: requestedSource.sourceType,
					sourceId: requestedSource.sourceId,
					cloud: effectiveScope.provider,
					region: effectiveScope.region,
					account: effectiveScope.account,
					tenantId: tenantId || undefined,
				},
				signal
			).then((response) => response.data);
		},
		[api, tenantId, requestedSource.sourceType, requestedSource.sourceId, effectiveScope]
	);
	const topologyResult = useInsightAsync<InsightEnvelope<InsightTopologyResponse>>(
		effectiveScope ? topologyFetcher : null,
		[
			tenantId,
			requestedSource.sourceType,
			requestedSource.sourceId,
			effectiveScope?.provider,
			effectiveScope?.region,
			effectiveScope?.account,
		],
		{
			// CTX-422: `state` names the cause, so a result the backend already
			// called empty is empty regardless of what `nodes` happens to hold —
			// no longer inferred from `nodes.length` alone.
			isEmpty: (envelope) =>
				envelope.data.state === "not_processed" ||
				envelope.data.state === "topology_unavailable" ||
				envelope.data.nodes.length === 0,
		}
	);

	// Remember the cluster's member list from the merged response so the switcher
	// keeps every tab after the user scopes down to one member.
	useEffect(() => {
		if (memberCtxrUuid !== null) return;
		const provenance = topologyResult.data?.data.provenance;
		if (topologyResult.data?.data.sourceType === "cluster" && provenance && provenance.length > 0) {
			setClusterMembers(provenance);
		}
	}, [memberCtxrUuid, topologyResult.data]);

	const findingsFetcher = useCallback(
		(signal: AbortSignal) => {
			if (!requestedSource.sourceType || requestedSource.sourceId == null || !effectiveScope) {
				return Promise.reject(new Error("scope incomplete"));
			}
			return getFindings(
				api,
				{
					sourceType: requestedSource.sourceType,
					sourceId: requestedSource.sourceId,
					cloud: effectiveScope.provider,
					region: effectiveScope.region,
					account: effectiveScope.account,
					tenantId: tenantId || undefined,
				},
				signal
			).then((response) => response.data);
		},
		[api, tenantId, requestedSource.sourceType, requestedSource.sourceId, effectiveScope]
	);
	const findingsResult = useInsightAsync<InsightEnvelope<InsightFindingsResponse>>(
		effectiveScope && activeMode !== "topology" ? findingsFetcher : null,
		[
			tenantId,
			requestedSource.sourceType,
			requestedSource.sourceId,
			effectiveScope?.provider,
			effectiveScope?.region,
			effectiveScope?.account,
			scope.mode,
		],
		{ isEmpty: (envelope) => envelope.data.security.findings.length === 0 && envelope.data.optimization.findings.length === 0 }
	);

	// Canvas vs List representation of the same topology. A flat backbone source
	// (dozens of root-parented route tables/gateways, no VPC containment) is
	// unreadable as a ReactFlow card wall, so it *defaults* to the dense List;
	// a normal nested VPC topology keeps the canvas. The user's explicit toggle
	// (`representationOverride`) always wins over this per-source default.
	const autoRepresentation = shouldDefaultToList(topologyResult.data?.data) ? "list" : "canvas";
	const representation: "canvas" | "list" = representationOverride ?? autoRepresentation;

	// CTX-112: the scope summary, Topology/Findings switch, and resource search
	// used to be a second stacked toolbar row above the canvas — they're built
	// here instead and handed to the shared page-header slot (see
	// `PageHeaderActions`) so they render beside "My View" in the app's existing
	// title band. The scope-browser collapse toggle used to live here too, but
	// now sits on the panel it controls (see `InsightScopeBrowser`'s `onCollapse`
	// and the expand rail below). Search stays lifted at this view boundary (not
	// owned by `InsightTopologyCanvas`) so this one input can live in the header
	// while still controlling the active representation below.
	const headerActions = useMemo(
		() => (
			<div className="flex min-w-0 flex-1 flex-wrap items-center gap-3">
				{scope.sourceName && (
					<span
						data-testid="active-path-label"
						className="min-w-0 truncate text-sm font-medium text-semantic-text-primary"
					>
						{scope.sourceName}
						{/*
							The scope actually being shown, which is the processed one — not
							necessarily the inventory ancestry the sidebar clicked through
							(CTX-424).
						*/}
						{effectiveScope && (
							<span className="text-semantic-text-tertiary"> / {formatScope(effectiveScope)}</span>
						)}
					</span>
				)}
				{/*
					The request was pointed at a scope this source never processed, and
					it has exactly one that it did — say so, so the account on screen not
					matching the one in the tree is explained rather than surprising.
				*/}
				{scopeResolution?.kind === "redirected" && (
					<span
						data-testid="scope-redirected-note"
						className="shrink-0 text-xs text-semantic-text-tertiary"
						title={`Requested ${formatScope(scopeResolution.requested)}`}
					>
						showing the only processed scope for this source
					</span>
				)}
				{effectiveScope && (
					<GenericTabs
						aria-label="Topology representation"
						tabs={[
							{ label: "Canvas", value: "canvas" },
							{ label: "List", value: "list" },
						]}
						activeTab={representation}
						onTabChange={(value) => setRepresentationOverride(value as "canvas" | "list")}
						size="xs"
						fullWidth={false}
						fitContent
						className="shrink-0"
					/>
				)}
	
			</div>
		),
		[scope.sourceName, effectiveScope, scopeResolution, representation]
	);
	usePageHeaderActions(headerActions);

	return (
		<InsightLayout showTrialBanner={false}>
			{/* One-time product presentation tour (CTX-97 launch); self-dismisses via localStorage. */}
			<NetworkTopologyTour />
			{/*
				No gap and no outer padding on this row, matching BGP Visibility: the
				only separator between browser and canvas is the panel's own 1px
				`border-r`. A `gap` here let the page background show through as a wide
				grey band beside the panel (2026-08-06). The canvas column carries its
				own padding instead.
			*/}
			<div className="flex h-full min-h-0 w-full overflow-hidden">
				{/*
					The browser stays mounted and animates its width away instead of
					unmounting, so collapsing/expanding slides rather than snapping —
					and the cached inventory tree (plus its expanded branches) survives
					a collapse. `visibility` is transitioned alongside width/opacity on
					purpose: it flips to hidden only at the END of the hide transition
					(so the fade is visible) while returning instantly on expand, and
					unlike `opacity-0` alone it takes the collapsed panel out of the tab
					order instead of leaving invisible focusable rows behind.
				*/}
				<aside
					data-testid="scope-browser-panel"
					aria-hidden={sidebarCollapsed}
					className={`shrink-0 overflow-hidden border-semantic-border-secondary bg-semantic-bg-primary transition-[width,opacity,visibility] duration-300 ease-in-out motion-reduce:transition-none ${
						sidebarCollapsed ? "invisible w-0 border-r-0 opacity-0" : "visible w-[340px] border-r opacity-100"
					}`}
				>
					<InsightScopeBrowser scope={scope} onActivateScope={activateScope} />
				</aside>

				{/* Collapse handle straddling the browser/canvas divider — the same
				    control BGP Visibility uses. Always mounted (flips direction), so
				    it is the single collapse/expand affordance for the panel. */}
				<div className="relative z-20 w-0 flex-shrink-0">
					<button
						type="button"
						data-testid="scope-browser-collapse-handle"
						onClick={() => setSidebarCollapsed((prev) => !prev)}
						title={sidebarCollapsed ? "Show sources" : "Hide sources"}
						aria-label={sidebarCollapsed ? "Show source browser" : "Hide source browser"}
						aria-expanded={!sidebarCollapsed}
						className={`absolute top-1/2 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-full border border-semantic-border-secondary bg-semantic-bg-primary text-semantic-text-secondary shadow-sm transition-colors hover:bg-semantic-state-active hover:text-semantic-text-primary ${
							sidebarCollapsed ? "translate-x-1" : "-translate-x-1/2"
						}`}
					>
						{sidebarCollapsed ? <ChevronRight size={14} /> : <ChevronLeft size={14} />}
					</button>
				</div>

				<div className="flex min-h-0 min-w-0 flex-1 flex-col gap-2 p-3">
					{!hasSource && (
						<div
							data-testid="no-source-selected"
							className="flex flex-1 items-center justify-center rounded-sm border border-dashed border-semantic-border-secondary text-sm text-semantic-text-secondary"
						>
							Select a Reflector or Cluster to begin.
						</div>
					)}

					{/*
						This source has processed nothing, so no scope can render — say
						that instead of handing back an empty canvas the user would read
						as "no resources here" (CTX-424).
					*/}
					{hasSource && scopeResolution?.kind === "none" && (
						<div
							data-testid="no-processed-scope"
							className="flex flex-1 items-center justify-center rounded-sm border border-dashed border-semantic-border-secondary px-6 text-center text-sm text-semantic-text-secondary"
						>
							No discovery data has been processed for this source yet, so there is no topology to show for any
							cloud, region or account.
						</div>
					)}

					{/*
						Several processed scopes and the requested one is not among them —
						only the user can say which they meant.
					*/}
					{hasSource && scopeResolution?.kind === "ambiguous" && (
						<div
							data-testid="ambiguous-scope"
							className="flex flex-1 flex-col items-center justify-center gap-2 rounded-sm border border-dashed border-semantic-border-secondary px-6 text-center text-sm text-semantic-text-secondary"
						>
							<span>
								This source has processed data for more than one scope, and the selected one is not among them.
								Pick one from the browser:
							</span>
							<ul className="list-none space-y-1 font-medium text-semantic-text-primary">
								{scopeResolution.scopes.map((candidate) => (
									<li key={formatScope(candidate)}>{formatScope(candidate)}</li>
								))}
							</ul>
						</div>
					)}

					{hasSource && !effectiveScope && scopesSettled && scopeResolution == null && (
						<div
							data-testid="incomplete-scope"
							className="flex flex-1 items-center justify-center rounded-sm border border-dashed border-semantic-border-secondary text-sm text-semantic-text-secondary"
						>
							This link is missing cloud/region information — reselect a source from the browser.
						</div>
					)}

					{effectiveScope && (
						<div className="flex min-h-0 flex-1 gap-3">
							<div className="flex min-h-0 min-w-0 flex-1 flex-col gap-2">
								{activeMode === "topology" ? (
									<>
										{/*
											Gated on `status === "success"` (not merely `data` truthiness):
											`useInsightAsync` clears `data` the instant a new request starts
											or fails, but the "empty" case (zero usable nodes) still carries a
											matching `data` payload — that must render the blocking
											`describeEmptyTopology` message below, never this non-blocking
											"showing partial data" banner alongside it.
										*/}
										{topologyResult.status === "success" && topologyResult.data && (
											<>
												<StatusBanner
													partial={topologyResult.data.data.partial}
													unavailableMembers={topologyResult.data.data.unavailableMembers}
												/>
												<CoverageWarningBanner
													warnings={topologyResult.data.data.coverageWarnings}
													provenance={topologyResult.data.data.provenance}
												/>
											</>
										)}
										{representation === "list" ? (
											<InsightResourceList
												topology={topologyResult.data?.data ?? null}
												status={topologyResult.status}
												errorMessage={topologyResult.errorMessage}
												onRetry={topologyResult.refetch}
												selectedResourceId={scope.selectedResourceId}
												onSelectResource={selectResource}
												search={search}
												emptyDescription={
													topologyResult.status === "empty"
														? describeEmptyState(topologyResult.data?.data).description
														: undefined
												}
											/>
										) : (
											// The member switcher floats over the canvas (BGP Visibility's
											// placement) rather than living in the sidebar: the sidebar picks
											// the SOURCE, this picks which member of that source you are
											// looking at. Canvas-only, like BGP's — the List representation
											// is an all-members view by nature.
											<div className="relative flex min-h-0 flex-1">
												<ClusterMemberSelector
													members={clusterMembers}
													activeMemberCtxrUuid={memberCtxrUuid}
													onSelectMember={setMemberCtxrUuid}
												/>
												<InsightTopologyCanvas
													topology={topologyResult.data?.data ?? null}
													status={topologyResult.status}
													errorMessage={topologyResult.errorMessage}
													onRetry={topologyResult.refetch}
													selectedResourceId={scope.selectedResourceId}
													onSelectResource={selectResource}
													search={search}
													// The canvas derives its own title and message from
													// `state`; nothing to override here.
												/>
											</div>
										)}
									</>
								) : (
									<FindingsPanel
										findings={findingsResult.data?.data ?? null}
										status={findingsResult.status}
										errorMessage={findingsResult.errorMessage}
										onRetry={findingsResult.refetch}
										selectedResourceId={scope.selectedResourceId}
									/>
								)}
							</div>
							{activeMode === "topology" && (
								<ResourceDetailPanel
									topology={topologyResult.data?.data ?? null}
									selectedResourceId={scope.selectedResourceId}
									onClose={() => selectResource(null)}
									onSelectResource={selectResource}
								/>
							)}
						</div>
					)}
				</div>
			</div>
		</InsightLayout>
	);
};

export default InsightTopologyViewV2;
